import type { ReactNode } from "react";

import { HeroDisc } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";

/**
 * The session that cannot be renewed, which neither design file draws.
 *
 * It matters more on a phone than on a console. A courier mid-shift whose refresh token has aged out
 * is a courier who will stop receiving offers, and the offer stream says so in its own words —
 * `token_expired` with `reconnectable: false`. The one thing this screen must not do is look like a
 * screen that is still listening.
 *
 * `lastedMs` and `endedAt` come from the authority rather than from a clock here, so the figures are
 * the session's own.
 */
export function SessionEnded({
  endedAt,
  lastedMs,
  onSignInAgain,
}: {
  readonly endedAt: number;
  readonly lastedMs: number;
  readonly onSignInAgain: () => void;
}): ReactNode {
  const ended = new Date(endedAt);
  const clock = `${String(ended.getHours()).padStart(2, "0")}:${String(ended.getMinutes()).padStart(2, "0")}`;
  const hours = Math.floor(lastedMs / 3_600_000);
  const minutes = Math.round((lastedMs % 3_600_000) / 60_000);
  const lasted = hours > 0 ? `${String(hours)} h ${String(minutes)} min` : `${String(minutes)} min`;

  return (
    <PhoneShell
      screen="readiness screen--state"
      art={<HeroDisc glyph="lock_clock" caption="Session ended" />}
      online={false}
      earningsLabel={null}
      chip={{ class: "closed", label: "session ended" }}
      title="You've been signed out"
      sub="Offers won't reach you until you sign in again. Nothing you did is lost."
      rows={[
        { key: "Ended at", value: clock },
        { key: "Lasted", value: lasted },
      ]}
      actions={[{ label: "Sign in again", glyph: "login", primary: true, onPress: onSignInAgain }]}
      flag="wording not in the design"
    />
  );
}
